import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, login, verify2fa, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot | 2fa
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [code, setCode] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const go = useCallback((u) => {
    if (["super_admin", "admin"].includes(u.role)) nav("/admin");
    else if (u.role === "owner") nav("/proprietaire");
    else if (u.role === "seller") nav("/espace-vente");
    else nav("/");
  }, [nav]);

  useEffect(() => {
    if (user && user !== false) go(user);
  }, [user, go]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const data = await login(form.email, form.password);
        if (data.twofa_required) {
          setPendingEmail(data.email);
          setMode("2fa");
          toast.success("Un code de connexion vous a été envoyé par email");
        } else {
          toast.success("Bienvenue !");
          go(data.user);
        }
      } else if (mode === "2fa") {
        const u = await verify2fa(pendingEmail, code);
        toast.success("Bienvenue !");
        go(u);
      } else if (mode === "register") {
        const u = await register(form);
        toast.success("Bienvenue !");
        go(u);
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success("Si un compte existe, un email de réinitialisation vient d'être envoyé.");
        setMode("login");
      }
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  const titles = { login: "Connexion", register: "Créer un compte", forgot: "Mot de passe oublié", "2fa": "Vérification" };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-20">
        <div className="bg-white border border-nj-olive rounded-lg p-8">
          <h1 className="font-serif font-light text-3xl text-nj-text text-center">{titles[mode]}</h1>
          <p className="text-center text-sm text-nj-muted mt-2">
            {mode === "2fa" ? `Saisissez le code envoyé à ${pendingEmail}`
              : mode === "forgot" ? "Recevez un lien de réinitialisation par email"
              : "Espace administrateur, propriétaire & voyageur"}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            {mode === "register" && (
              <>
                <div><Label>Nom complet</Label><Input required data-testid="reg-name" value={form.name} onChange={set("name")} className="mt-1.5" /></div>
                <div><Label>Téléphone</Label><Input value={form.phone} onChange={set("phone")} className="mt-1.5" /></div>
              </>
            )}

            {mode === "2fa" ? (
              <div>
                <Label>Code à 6 chiffres</Label>
                <Input required data-testid="twofa-code" value={code} onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric" maxLength={6} placeholder="000000" className="mt-1.5 tracking-[0.4em] text-center text-lg" />
              </div>
            ) : (
              <>
                <div><Label>Email</Label><Input required type="email" data-testid="login-email" value={form.email} onChange={set("email")} className="mt-1.5" /></div>
                {mode !== "forgot" && (
                  <div><Label>Mot de passe</Label><Input required type="password" data-testid="login-password" value={form.password} onChange={set("password")} className="mt-1.5" /></div>
                )}
              </>
            )}

            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-nj-primary hover:bg-nj-primaryHover text-white rounded-sm py-6">
              {loading ? "…"
                : mode === "login" ? "Se connecter"
                : mode === "2fa" ? "Valider le code"
                : mode === "forgot" ? "Envoyer le lien"
                : "Créer mon compte"}
            </Button>
          </form>

          {mode === "login" && (
            <div className="mt-5 space-y-2 text-center">
              <button onClick={() => setMode("forgot")} data-testid="forgot-link" className="block w-full text-sm text-nj-muted hover:text-nj-primary">Mot de passe oublié ?</button>
              <button onClick={() => setMode("register")} className="block w-full text-sm text-nj-primary font-medium">Pas encore de compte ? Créer un compte</button>
            </div>
          )}
          {(mode === "register" || mode === "forgot" || mode === "2fa") && (
            <button onClick={() => { setMode("login"); setCode(""); }} className="w-full text-center text-sm text-nj-primary mt-5 font-medium">
              ← Retour à la connexion
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
}
